import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import CampoTexto from '@/components/base/CampoTexto';
import CampoArea from '@/components/base/CampoArea';
import OpcaoSelecao from '@/components/base/OpcaoSelecao';
import { TIPOS_ENCONTRO } from '../constants';
import { mensagemErro, separarDataHora } from '../utils';
import type { Encontro } from '../types';

interface FormEncontroProps {
  eventoId: number;
  cidade: string;
  encontro: Encontro | null;
  aoFechar: () => void;
  aoSalvar: () => void;
}

interface Modalidade {
  id: number;
  nome: string;
}

interface Conflito {
  nome: string;
  evento_pai: string | null;
}

const inputCls =
  'w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500';

export default function FormEncontro({
  eventoId,
  cidade,
  encontro,
  aoFechar,
  aoSalvar,
}: FormEncontroProps) {
  const editando = !!encontro;
  const ini = separarDataHora(encontro?.inicio_em || '');
  const fim = separarDataHora(encontro?.fim_em || '');

  const [nome, setNome] = useState(encontro?.nome || '');
  const [tipo, setTipo] = useState(encontro?.tipo || 'formacao');
  const [inicioData, setInicioData] = useState(ini.data);
  const [inicioHora, setInicioHora] = useState(ini.hora);
  const [fimData, setFimData] = useState(fim.data);
  const [fimHora, setFimHora] = useState(fim.hora);
  const [local, setLocal] = useState(encontro?.local_nome || '');
  const [obrigatorio, setObrigatorio] = useState(encontro ? encontro.obrigatorio : true);
  const [observacao, setObservacao] = useState(encontro?.descricao || '');
  const [modalidades, setModalidades] = useState<number[]>(encontro?.modalidade_ids || []);

  const [mods, setMods] = useState<Modalidade[]>([]);
  const [conflitos, setConflitos] = useState<Conflito[]>([]);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let ativo = true;
    supabase
      .from('modalidades')
      .select('id, nome')
      .eq('evento_id', eventoId)
      .order('ordem')
      .then(({ data }) => {
        if (ativo) setMods((data as Modalidade[]) || []);
      });
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  function alternarModalidade(id: number) {
    setModalidades((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function verificarConflito(data: string) {
    if (!data || !cidade.trim()) {
      setConflitos([]);
      return;
    }
    const inicioDia = new Date(`${data}T00:00:00`).toISOString();
    const fimDia = new Date(`${data}T23:59:59`).toISOString();
    const { data: itens } = await supabase
      .from('vw_agenda')
      .select('nome, evento_pai')
      .eq('cidade', cidade)
      .neq('evento_id', eventoId)
      .gte('inicio_em', inicioDia)
      .lte('inicio_em', fimDia);
    setConflitos((itens as Conflito[]) || []);
  }

  async function salvar() {
    const novos: Record<string, string> = {};
    if (!nome.trim()) novos.nome = 'Digite o nome desta data.';
    if (!inicioData) novos.inicioData = 'Escolha a data.';
    if (!inicioHora) novos.inicioHora = 'Escolha o horário.';
    setErros(novos);
    if (Object.keys(novos).length > 0) return;

    const inicioISO = new Date(`${inicioData}T${inicioHora}:00`).toISOString();
    let fimISO: string | null = null;
    if (fimData && fimHora) {
      fimISO = new Date(`${fimData}T${fimHora}:00`).toISOString();
    }

    setSalvando(true);
    const { error } = await supabase.rpc('salvar_encontro', {
      p_evento_id: eventoId,
      p_nome: nome.trim(),
      p_inicio_em: inicioISO,
      p_fim_em: fimISO,
      p_tipo: tipo,
      p_descricao: observacao.trim() || null,
      p_local_nome: local.trim() || null,
      p_obrigatorio: obrigatorio,
      p_modalidades: modalidades.length ? modalidades : null,
      p_encontro_id: encontro ? encontro.id : null,
    });
    setSalvando(false);
    if (error) {
      setErros({ geral: mensagemErro(error, 'Não foi possível salvar. Tente novamente.') });
      return;
    }
    aoSalvar();
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={aoFechar} />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-[560px] flex-col border-l border-secondary-200 bg-white">
        <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
          <h3 className="text-lg font-normal text-foreground-950">
            {editando ? 'Editar data' : 'Adicionar data'}
          </h3>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          {conflitos.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
              <p className="flex items-center gap-2 text-sm font-medium text-amber-800">
                <i className="ri-alert-line" />
                Neste dia já tem em {cidade}:
              </p>
              <ul className="mt-2 space-y-1">
                {conflitos.map((c, i) => (
                  <li key={i} className="text-sm text-amber-800">
                    • {c.nome}
                    {c.evento_pai ? ` — ${c.evento_pai}` : ''}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-sm text-amber-800">Pode marcar mesmo assim.</p>
            </div>
          )}

          {erros.geral && (
            <p
              className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700"
              role="alert"
              data-erro="true"
            >
              {erros.geral}
            </p>
          )}

          <CampoTexto
            id="encontro-nome"
            name="nome"
            label="Nome"
            value={nome}
            onChange={(v) => {
              setNome(v);
              if (erros.nome) setErros((e) => ({ ...e, nome: '' }));
            }}
            required
            error={erros.nome || null}
            placeholder="1ª Formação"
          />

          <div>
            <span className="block text-base font-medium text-foreground-950">O que é</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {TIPOS_ENCONTRO.map((t) => {
                const ativa = tipo === t.valor;
                return (
                  <button
                    key={t.valor}
                    type="button"
                    onClick={() => setTipo(t.valor)}
                    className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors ${
                      ativa
                        ? 'border-primary-500 bg-primary-500 text-white'
                        : 'border-secondary-300 bg-white text-foreground-800 hover:border-primary-400'
                    }`}
                  >
                    {t.rotulo}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-base font-medium text-foreground-950">
                Começa <span className="text-danger-600">*</span>
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  type="date"
                  value={inicioData}
                  onChange={(e) => {
                    const v = e.target.value;
                    setInicioData(v);
                    if (erros.inicioData) setErros((er) => ({ ...er, inicioData: '' }));
                    verificarConflito(v);
                  }}
                  className={`${inputCls} ${erros.inicioData ? 'border-danger-500' : ''}`}
                />
                <input
                  type="time"
                  value={inicioHora}
                  onChange={(e) => {
                    setInicioHora(e.target.value);
                    if (erros.inicioHora) setErros((er) => ({ ...er, inicioHora: '' }));
                  }}
                  className={`${inputCls} ${erros.inicioHora ? 'border-danger-500' : ''}`}
                />
              </div>
              {(erros.inicioData || erros.inicioHora) && (
                <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
                  {erros.inicioData || erros.inicioHora}
                </p>
              )}
            </div>

            <div>
              <label className="block text-base font-medium text-foreground-950">Termina</label>
              <p className="mt-1 text-sm text-secondary-500">Pode ficar em branco.</p>
              <div className="mt-1 flex gap-2">
                <input
                  type="date"
                  value={fimData}
                  onChange={(e) => setFimData(e.target.value)}
                  className={inputCls}
                />
                <input
                  type="time"
                  value={fimHora}
                  onChange={(e) => setFimHora(e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          <CampoTexto
            id="encontro-local"
            name="local"
            label="Onde"
            help="Em branco = no mesmo lugar do evento."
            value={local}
            onChange={setLocal}
            placeholder="Igreja São João Batista"
          />

          <div>
            <span className="block text-base font-medium text-foreground-950">Para quem</span>
            <p className="mt-1 text-sm text-secondary-500">
              Nenhum marcado = para todos do evento.
            </p>
            {mods.length > 0 ? (
              <div className="mt-1">
                {mods.map((m) => (
                  <OpcaoSelecao
                    key={m.id}
                    tipo="checkbox"
                    checked={modalidades.includes(m.id)}
                    onChange={() => alternarModalidade(m.id)}
                  >
                    {m.nome}
                  </OpcaoSelecao>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm text-secondary-500">
                Este evento ainda não tem formas de participar.
              </p>
            )}
          </div>

          <div>
            <span className="block text-base font-medium text-foreground-950">Presença</span>
            <div className="mt-1">
              <OpcaoSelecao tipo="radio" checked={obrigatorio} onChange={() => setObrigatorio(true)}>
                Obrigatória
              </OpcaoSelecao>
              <OpcaoSelecao
                tipo="radio"
                checked={!obrigatorio}
                onChange={() => setObrigatorio(false)}
              >
                Facultativa
              </OpcaoSelecao>
            </div>
          </div>

          <CampoArea
            id="encontro-obs"
            name="observacao"
            label="Observação"
            value={observacao}
            onChange={setObservacao}
          />
        </div>

        <div className="border-t border-secondary-100 px-5 py-4">
          <button
            type="button"
            onClick={salvar}
            disabled={salvando}
            className="flex h-12 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 text-[15px] font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {salvando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Salvar data'}
          </button>
        </div>
      </div>
    </div>
  );
}